import type { Delta, HubMetrics, MinuteSeries } from '@dyspatch/core';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { BarChart3 } from 'lucide-react';
import type { ReactNode } from 'react';
import { RowSkeleton, TaskKey } from '@/components/common/bits';
import { Page, ViewHeader } from '@/components/layout/Page';
import { Button } from '@/components/ui/button';
import { Menu } from '@/components/ui/menu';
import { Panel, PanelHead } from '@/components/ui/panel';
import { BreakdownTable } from '@/features/metrics/breakdown';
import {
  ChartLegend,
  LeadTimeChart,
  PickupDecisionChart,
  QueueDepthChart,
  BarChart as SeriesBarChart,
} from '@/features/metrics/charts';
import { ReviewMetricsStrip } from '@/features/review/ReviewMetrics';
import { useMetrics } from '@/hooks/use-fleet';
import { duration, formatTokens, formatUsd, percent } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { MetricsSearch } from '@/router';

/**
 * The window view: what the fleet produced, who used it, whether the record
 * holds. Every other page is about this minute; this one is about the last
 * seven, thirty or ninety days, beside the same span before it.
 *
 * The page is deliberately plain. It exists to be read out loud in a review
 * ("two merged PRs this week, both overnight, one recorded by GitHub") and to
 * make an empty fleet look empty. PRODUCT.md is explicit that no usage numbers
 * existed before this page; it must never manufacture any, which is why every
 * tile shows a dash rather than a zero when there is nothing to measure from.
 *
 * It leads with the business translation and not with the platform counters.
 * "Is this worth the money" is the question the page is opened to answer, and
 * cost per merged PR answers it; total agent minutes is a fact about the
 * machines that only means something once the first question is settled, so it
 * sits further down. The two scatter plots that used to take a panel each went
 * for the same reason: a cloud of one dot per run against task size showed
 * nothing anybody could act on.
 *
 * Every control is in the URL - the window, both filters, the table order - so
 * a slice somebody found is a link they can paste to the person who should see
 * it.
 */

export const METRICS_RANGES = [1, 7, 14, 30, 90] as const;
export type MetricsRange = (typeof METRICS_RANGES)[number];
export const DEFAULT_METRICS_RANGE: MetricsRange = 7;

export function isMetricsRange(value: unknown): value is MetricsRange {
  return METRICS_RANGES.includes(value as MetricsRange);
}

// ---------------------------------------------------------------------------
// Small pieces
// ---------------------------------------------------------------------------

/** "+3 vs 1" beside a headline, or "same as before". Nothing when both are zero. */
function Trend({ delta }: { delta: Delta }) {
  if (delta.current === 0 && delta.previous === 0) return null;
  const diff = delta.current - delta.previous;
  const tone = diff > 0 ? 'text-ok' : diff < 0 ? 'text-warn' : 'text-fg-3';
  const sign = diff > 0 ? '+' : '';
  return (
    <span className={cn('tnum', tone)}>
      {diff === 0 ? 'same as before' : `${sign}${diff} vs ${delta.previous} before`}
    </span>
  );
}

interface TileSpec {
  label: string;
  value: ReactNode;
  note?: ReactNode;
  tone?: string;
}

function Tile({ tile }: { tile: TileSpec }) {
  return (
    <div className="min-w-0 px-3.5 py-2.5">
      <div className="dy-eyebrow truncate">{tile.label}</div>
      <div className={cn('tnum mt-1 text-lg leading-none font-semibold', tile.tone)}>
        {tile.value}
      </div>
      {tile.note ? <div className="text-fg-3 mt-1 truncate text-2xs">{tile.note}</div> : null}
    </div>
  );
}

function Strip({
  title,
  hue,
  tiles,
  columns,
}: {
  title?: string;
  hue?: string;
  tiles: TileSpec[];
  columns: 4 | 5 | 6;
}) {
  const grid = {
    4: 'sm:grid-cols-2 xl:grid-cols-4',
    5: 'sm:grid-cols-3 xl:grid-cols-5',
    6: 'sm:grid-cols-3 xl:grid-cols-6',
  }[columns];
  return (
    <Panel>
      {title ? <PanelHead title={title} hue={hue} /> : null}
      <div className={cn('grid grid-cols-2 divide-x divide-y xl:divide-y-0', grid)}>
        {tiles.map((tile) => (
          <Tile key={tile.label} tile={tile} />
        ))}
      </div>
    </Panel>
  );
}

/** The value of the `All` option, which is what clears a filter from the URL. */
const ALL = '';

/**
 * What one control changes. Null clears a filter, an absent key leaves it
 * alone, and `setSearch` folds either into the search it already has - so a
 * person picking a project keeps the window and the table order they chose.
 */
interface SearchPatch {
  days?: MetricsRange;
  repo?: string | null;
  person?: string | null;
}

/** One of the two slice selects. `All` is always offered, filtered or not. */
function FilterMenu({
  label,
  value,
  options,
  allLabel,
  onChange,
}: {
  label: string;
  value: string | null;
  options: readonly string[];
  allLabel: string;
  onChange: (value: string | null) => void;
}) {
  // A filter read off the URL is live before the response that lists the
  // options has landed, so it is added to the list when it is not in it yet.
  // Without this the trigger falls back to its own label on a reload, and the
  // page would say "Project" while showing one project's numbers.
  const known = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <Menu
      label={label}
      align="end"
      value={value ?? ALL}
      onChange={(next) => onChange(next === ALL ? null : next)}
      options={[
        { value: ALL, label: allLabel },
        ...known.map((option) => ({ value: option, label: option })),
      ]}
    />
  );
}

/** A series as "median · p90 over n", or a dash when nothing was measured. */
function series(s: MinuteSeries): { value: string; note: string } {
  if (s.count === 0) return { value: '-', note: 'nothing measured yet' };
  return {
    value: duration(s.medianMinutes),
    note: `median over ${s.count} · p90 ${duration(s.p90Minutes)} · longest ${duration(s.longestMinutes)}`,
  };
}

function hours(minutes: number): string {
  if (minutes < 60) return `${minutes}m`;
  return `${(minutes / 60).toFixed(minutes >= 600 ? 0 : 1)}h`;
}

function pad(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function MetricsPage() {
  const search = useSearch({ strict: false }) as MetricsSearch;
  const navigate = useNavigate();
  const days = search.days ?? DEFAULT_METRICS_RANGE;
  const repo = search.repo ?? null;
  const person = search.person ?? null;
  const query = useMetrics(days, repo, person);
  const m = query.data;

  /**
   * Every control writes the whole search through one path, so the window, the
   * two filters and the table order cannot get out of step with each other.
   * `replace` because none of this is a place: Back should leave the page, not
   * walk the reader back out through six windows they scrubbed through.
   */
  const setSearch = (patch: SearchPatch) => {
    const next = { ...search, ...patch };
    void navigate({
      to: '/metrics',
      // The default window is left out, so a link to this page is a link to the
      // page rather than to a button nobody pressed.
      search: {
        ...next,
        days: next.days === DEFAULT_METRICS_RANGE ? undefined : next.days,
        repo: next.repo || undefined,
        person: next.person || undefined,
      },
      replace: true,
    });
  };

  const applied = m?.slice.applied;
  // What the reader is looking at, said in the console's own words: the API
  // calls it a repo, a person reads "project".
  const narrowed = [
    applied?.repo ? `${applied.repo} only` : null,
    applied?.person ? `filed by ${applied.person}` : null,
  ].filter(Boolean);
  const windowLabel = days === 1 ? 'last 24 hours' : `last ${m?.window.days ?? days} days`;
  const sub = m
    ? [
        windowLabel,
        ...narrowed,
        `off hours ${pad(m.offHours.eveningFrom)} to ${pad(m.offHours.morningUntil)} ${m.offHours.timezone}${m.offHours.weekends ? ' and weekends' : ''}`,
      ].join(' · ')
    : undefined;

  return (
    <>
      <ViewHeader
        title="Metrics"
        icon={BarChart3}
        tone="var(--dy-info)"
        sub={sub}
        actions={
          <div className="flex flex-wrap items-center justify-end gap-1">
            {METRICS_RANGES.map((range) => (
              <Button
                key={range}
                size="xs"
                variant={range === days ? 'subtle' : 'ghost'}
                aria-pressed={range === days}
                aria-label={range === 1 ? 'Last 24 hours' : `Last ${range} days`}
                onClick={() => setSearch({ days: range })}
              >
                {range === 1 ? '24h' : `${range}d`}
              </Button>
            ))}
            {/* The options come off the unfiltered window (see `HubMetricsSlice`),
                so picking one never collapses the list it was picked from and a
                reader who filtered themselves out can still get back. */}
            <FilterMenu
              label="Project"
              allLabel="All projects"
              value={repo}
              options={m?.slice.repos ?? []}
              onChange={(next) => setSearch({ repo: next })}
            />
            <FilterMenu
              label="Person"
              allLabel="All people"
              value={person}
              options={m?.slice.people ?? []}
              onChange={(next) => setSearch({ person: next })}
            />
          </div>
        }
      />
      <Page className="dy-stack">
        {m ? (
          <Body m={m} onFilter={setSearch} />
        ) : query.isError ? (
          <Panel>
            <p className="text-bad px-gutter py-4 text-xs">
              The hub did not answer: {(query.error as Error).message}
            </p>
          </Panel>
        ) : (
          <Panel>
            <RowSkeleton rows={4} />
          </Panel>
        )}
      </Page>
    </>
  );
}

function Body({ m, onFilter }: { m: HubMetrics; onFilter: (patch: SearchPatch) => void }) {
  const merged = m.northStar.mergedPrs;
  const nothingYet = merged.current === 0 && m.adoption.jobs.filed.current === 0;
  const filedToPr = series(m.value.leadTime.filedToPr);
  const filedToMerge = series(m.value.leadTime.filedToMerge);
  const approval = series(m.trust.approvalLatency);
  const unit = m.value.unitEconomics;

  /**
   * The first strip answers "is this worth the money" before anything answers
   * "what did the machines do". Each of the three unit economics tiles is null
   * rather than zero whenever its denominator is zero or no run said what it
   * cost, and a null prints as a dash with a note saying which of those it is:
   * "free" and "unknown" are different claims and PRODUCT.md forbids the page
   * making the first one on the second one's evidence.
   */
  const headline: TileSpec[] = [
    {
      label: 'Merged PRs',
      value: merged.current,
      note: <Trend delta={merged} />,
      tone: merged.current ? 'text-ok' : undefined,
    },
    {
      label: 'Cost per merged PR',
      value: unit.costPerMergedPrNanoUsd === null ? '-' : formatUsd(unit.costPerMergedPrNanoUsd),
      note:
        unit.costPerMergedPrNanoUsd === null
          ? merged.current === 0
            ? 'nothing merged in this window'
            : 'no run reported a cost'
          : `${formatUsd(m.adoption.tasks.costNanoUsd.current)} over ${merged.current} merged`,
    },
    {
      label: 'Agent time per merged PR',
      value:
        unit.agentMinutesPerMergedPr === null
          ? '-'
          : duration(Math.round(unit.agentMinutesPerMergedPr)),
      note:
        unit.agentMinutesPerMergedPr === null
          ? 'nothing merged in this window'
          : `${hours(m.adoption.tasks.agentMinutes.current)} over ${merged.current} merged`,
    },
    {
      label: 'Off hours',
      value: m.adoption.tasks.prsReady.current ? percent(m.value.offHours.prsReadyShare) : '-',
      note: m.adoption.tasks.prsReady.current
        ? `${m.value.offHours.prsReady} of ${m.adoption.tasks.prsReady.current} PRs · ${percent(m.value.offHours.agentMinutesShare)} of agent time`
        : 'share of PRs ready outside the office day',
      tone: m.value.offHours.prsReady ? 'text-info' : undefined,
    },
    {
      label: 'Jobs filed',
      value: m.adoption.jobs.filed.current,
      note: <Trend delta={m.adoption.jobs.filed} />,
    },
    {
      label: 'People filing',
      value: m.adoption.people.submitters.current,
      note: <Trend delta={m.adoption.people.submitters} />,
    },
  ];

  /** The raw counters. True, and second: they are the inputs to the strip above. */
  const platform: TileSpec[] = [
    {
      label: 'PRs ready',
      value: m.adoption.tasks.prsReady.current,
      note: <Trend delta={m.adoption.tasks.prsReady} />,
    },
    {
      label: 'Agent time',
      value: hours(m.adoption.tasks.agentMinutes.current),
      note: `${hours(m.adoption.tasks.agentMinutes.previous)} in the window before`,
    },
    {
      label: 'Cost',
      value: m.adoption.tasks.costNanoUsd.current
        ? formatUsd(m.adoption.tasks.costNanoUsd.current)
        : '-',
      note: m.adoption.tasks.costNanoUsd.current
        ? 'runs that reported no cost are not counted'
        : 'no run reported a cost',
    },
    {
      label: 'Tokens',
      value: m.adoption.tasks.tokensUsed.current
        ? formatTokens(m.adoption.tasks.tokensUsed.current)
        : '-',
      note: m.adoption.tasks.tokensUsed.current
        ? 'input, output and cache, combined'
        : 'no run reported tokens',
    },
    {
      label: 'Runs',
      value: m.adoption.tasks.runs,
      note: `${m.adoption.tasks.dispatched} dispatched · ${m.adoption.tasks.failed} failed`,
    },
  ];

  const value: TileSpec[] = [
    { label: 'Filed to PR', value: filedToPr.value, note: filedToPr.note },
    { label: 'Filed to merge', value: filedToMerge.value, note: filedToMerge.note },
    {
      label: 'Cost per PR ready',
      value: unit.costPerPrReadyNanoUsd === null ? '-' : formatUsd(unit.costPerPrReadyNanoUsd),
      note:
        unit.costPerPrReadyNanoUsd === null
          ? m.adoption.tasks.prsReady.current === 0
            ? 'no PR reached review'
            : 'no run reported a cost'
          : `over ${m.adoption.tasks.prsReady.current} PRs ready`,
    },
    {
      label: 'First attempt',
      value: m.value.quality.reachedReview ? percent(m.value.quality.firstAttemptPassRate) : '-',
      note: m.value.quality.reachedReview
        ? `${m.value.quality.firstAttemptPasses} of ${m.value.quality.reachedReview} PRs with no failed run first`
        : 'PRs whose first run succeeded',
    },
    {
      label: 'Rework',
      value: m.adoption.tasks.prsReady.current ? m.value.quality.changesRequested : '-',
      note: m.adoption.tasks.prsReady.current
        ? `${m.value.quality.reworkRate.toFixed(2)} send-backs per PR ready`
        : 'send-backs by a reviewer',
      tone: m.value.quality.changesRequested ? 'text-warn' : undefined,
    },
  ];

  const people: TileSpec[] = [
    {
      label: 'New',
      value: m.adoption.people.newSubmitters,
      note: 'first ever job in this window',
    },
    {
      label: 'Returning',
      value: m.adoption.people.retainedSubmitters,
      note: 'also filed in the window before',
    },
    { label: 'Approving', value: m.adoption.people.approvers, note: 'distinct people at gate one' },
    { label: 'Reviewing', value: m.adoption.people.reviewers, note: 'merged or sent back' },
  ];

  const fleet: TileSpec[] = [
    { label: 'Enrolled', value: m.adoption.fleet.devicesEnrolled, note: 'devices on the hub' },
    { label: 'Seen', value: m.adoption.fleet.devicesSeen, note: 'heartbeat in the window' },
    {
      label: 'Worked',
      value: m.adoption.fleet.devicesWorked,
      note: 'ran at least one task',
      tone: m.adoption.fleet.devicesWorked ? 'text-ok' : undefined,
    },
    {
      label: 'Recorded by GitHub',
      value: merged.current ? percent(m.value.merged.recordedBy.github / merged.current) : '-',
      note: merged.current
        ? `${m.value.merged.recordedBy.github} by webhook · ${m.value.merged.recordedBy.console} by Done`
        : 'share of merges the webhook reported',
    },
  ];

  const trust: TileSpec[] = [
    { label: 'Plan to Go', value: approval.value, note: approval.note },
    {
      label: 'Retries',
      value: m.trust.retries.retried + m.trust.retries.timedOut,
      note: `${m.trust.retries.retried} retried · ${m.trust.retries.timedOut} timed out · ${m.trust.retries.failedTerminal} failed for good`,
      tone: m.trust.retries.failedTerminal ? 'text-warn' : undefined,
    },
    {
      label: `Stuck > ${duration(m.trust.stuck.afterMinutes)}`,
      value: m.trust.stuck.count,
      note: 'open tasks with no movement',
      tone: m.trust.stuck.count ? 'text-bad' : undefined,
    },
    {
      label: 'Right now',
      value: m.trust.now.awaitingApproval + m.trust.now.inReview,
      note: `${m.trust.now.awaitingApproval} plans · ${m.trust.now.inReview} PRs waiting · ${m.trust.now.queued} queued · ${m.trust.now.running} running`,
      tone: m.trust.now.awaitingApproval + m.trust.now.inReview ? 'text-gate' : undefined,
    },
  ];

  return (
    <>
      <Strip tiles={headline} columns={6} />

      {nothingYet ? (
        <Panel>
          <p className="text-fg-2 px-gutter py-4 text-xs">
            Nothing was filed or merged in this window. The numbers below are real and they are
            zero: file a job, approve its plan, and let a device open a PR to move them.
          </p>
        </Panel>
      ) : null}

      <Panel>
        <PanelHead
          title="Throughput"
          hue="var(--dy-ok)"
          meta={`${m.window.days}d, one bar a day`}
        />
        <div className="grid gap-[var(--dy-gap)] px-gutter pb-3.5 sm:grid-cols-2">
          <div>
            <div className="dy-eyebrow mb-1.5">PRs merged and jobs filed</div>
            <SeriesBarChart
              label="PRs merged and jobs filed, per day"
              series={[
                { label: 'Merged', color: 'var(--dy-ok)', points: m.series.throughput.merged },
                { label: 'Filed', color: 'var(--dy-info)', points: m.series.throughput.filed },
              ]}
            />
            <ChartLegend
              items={[
                { label: 'Merged', color: 'var(--dy-ok)' },
                { label: 'Filed', color: 'var(--dy-info)' },
              ]}
            />
          </div>
          <div>
            <div className="dy-eyebrow mb-1.5">Retries and failures</div>
            <SeriesBarChart
              label="Retries, timeouts and failures, per day"
              series={[
                { label: 'Retried', color: 'var(--dy-warn)', points: m.series.retries.retried },
                { label: 'Timed out', color: 'var(--dy-plan)', points: m.series.retries.timedOut },
                { label: 'Failed', color: 'var(--dy-bad)', points: m.series.retries.failed },
              ]}
            />
            <ChartLegend
              items={[
                { label: 'Retried', color: 'var(--dy-warn)' },
                { label: 'Timed out', color: 'var(--dy-plan)' },
                { label: 'Failed', color: 'var(--dy-bad)' },
              ]}
            />
          </div>
        </div>
      </Panel>

      <Strip title="Value" hue="var(--dy-ok)" tiles={value} columns={5} />

      <Panel>
        <PanelHead title="Lead time" hue="var(--dy-ok)" meta="median line, p90 band" />
        <div className="grid gap-[var(--dy-gap)] px-gutter pb-3.5 sm:grid-cols-2">
          <div>
            <div className="dy-eyebrow mb-1.5">Filed to PR</div>
            <LeadTimeChart
              points={m.series.leadTime.filedToPr}
              label="Filed to PR, median and p90 by day"
              color="var(--dy-info)"
            />
          </div>
          <div>
            <div className="dy-eyebrow mb-1.5">Filed to merge</div>
            <LeadTimeChart
              points={m.series.leadTime.filedToMerge}
              label="Filed to merge, median and p90 by day"
              color="var(--dy-ok)"
            />
          </div>
        </div>
      </Panel>

      <div>
        <div className="dy-eyebrow px-1 pb-1.5">Review gate, this window</div>
        <ReviewMetricsStrip metrics={m.value.review} />
      </div>

      <Panel>
        <PanelHead title="Review gate over time" hue="var(--dy-gate)" />
        <div className="grid gap-[var(--dy-gap)] px-gutter pb-3.5 sm:grid-cols-2">
          <div>
            <div className="dy-eyebrow mb-1.5">Queue depth</div>
            <QueueDepthChart
              points={m.series.reviewGate.queueDepth}
              label="PRs waiting in review, by day"
              color="var(--dy-gate)"
            />
          </div>
          <div>
            <div className="dy-eyebrow mb-1.5">Pickup against decision, median</div>
            <PickupDecisionChart
              pickup={m.series.reviewGate.pickup}
              decision={m.series.reviewGate.decision}
              pickupColor="var(--dy-info)"
              decisionColor="var(--dy-plan)"
            />
            <ChartLegend
              items={[
                { label: 'Pickup', color: 'var(--dy-info)' },
                { label: 'Decision (dashed)', color: 'var(--dy-plan)' },
              ]}
            />
          </div>
        </div>
      </Panel>

      <Panel>
        <PanelHead title="Spend" hue="var(--dy-plan)" meta={`${m.window.days}d, one bar a day`} />
        <div className="px-gutter pb-3.5">
          <div className="dy-eyebrow mb-1.5">What the fleet spent, per day</div>
          {/* `dailyCost` is nanodollars in a `DailyCount`, which is why the
              caller says how to print the number: the chart draws a bar of the
              right height either way, but "1500000000 spend" is not a tooltip
              anybody can read. */}
          <SeriesBarChart
            label="Spend per day"
            format={(nanoUsd) => formatUsd(nanoUsd)}
            series={[{ label: 'Spend', color: 'var(--dy-plan)', points: m.series.dailyCost }]}
          />
          <p className="text-fg-3 mt-1.5 px-1 text-2xs">
            Nanodollars as dollars, summed over the runs that started that day. A run that reported
            no cost is not in this chart rather than counted as free (issue #18), so a quiet bar can
            mean nobody spent anything or that nothing said what it spent.
          </p>
        </div>
      </Panel>

      {/* The two tallies that used to sit here were a key and a count with no
          columns and no order. These are the same window, sortable, and each
          row is the way into its own slice. */}
      <BreakdownTable
        title="By project"
        keyLabel="Project"
        rows={m.breakdown.byRepo}
        empty="No project did anything in this window."
        active={m.slice.applied.repo}
        onPick={(key) => onFilter({ repo: key })}
      />
      <BreakdownTable
        title="By person"
        keyLabel="Person"
        rows={m.breakdown.byPerson}
        empty="Nobody filed or merged anything in this window."
        active={m.slice.applied.person}
        onPick={(key) => onFilter({ person: key })}
      />

      <Strip title="Platform" hue="var(--dy-accent)" tiles={platform} columns={5} />
      <Strip title="People" hue="var(--dy-gate)" tiles={people} columns={4} />
      <Strip title="Fleet" hue="var(--dy-info)" tiles={fleet} columns={4} />
      <Strip title="Trust" hue="var(--dy-plan)" tiles={trust} columns={4} />

      {m.trust.stuck.count ? (
        <Panel>
          <PanelHead
            title="Stuck tasks"
            meta={
              m.trust.stuck.count > m.trust.stuck.tasks.length
                ? `${m.trust.stuck.tasks.length} of ${m.trust.stuck.count}`
                : String(m.trust.stuck.count)
            }
          />
          <ul className="divide-y">
            {m.trust.stuck.tasks.map((task) => (
              <li key={task.id} className="flex items-center gap-2 px-gutter py-row text-xs">
                <Link to="/tasks/$taskId" params={{ taskId: task.id }} className="min-w-0 truncate">
                  <TaskKey briefId={task.briefId}>{task.key}</TaskKey>
                </Link>
                <span className="text-fg-3 truncate">{task.repo}</span>
                <span className="text-fg-3 truncate">{task.status}</span>
                <span className="tnum text-warn ml-auto shrink-0">
                  idle {duration(task.idleMinutes)}
                </span>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {m.adoption.people.submitterEmails.length ? (
        <p className="text-fg-3 px-1 text-2xs">
          Filing in this window: {m.adoption.people.submitterEmails.join(', ')}.
        </p>
      ) : null}
    </>
  );
}
